import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Printer } from 'lucide-react';
import { dateLabel } from '@adisys/shared';
import { api } from '../../lib/api';
import { qtyLabel } from '../../lib/crm';
import { Button, ErrorState, Skeleton } from '../../components/ui';

export function ChallanDocumentPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ['crm', 'challan', id], queryFn: () => api.get(`/crm/challans/${id}`) });
  if (q.isLoading) return <div className="p-8"><Skeleton className="h-96 w-full" /></div>;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const c = q.data;

  return (
    <div className="min-h-dvh bg-ink-100 py-6 print:bg-white print:py-0">
      <style>{'@page { size: A4; margin: 12mm; } @media print { body { background: #fff; } }'}</style>
      <div className="no-print mx-auto mb-4 flex max-w-[210mm] justify-between px-2">
        <Button icon={<ArrowLeft className="h-4 w-4" />} onClick={() => navigate(-1)}>Back</Button>
        <Button variant="primary" icon={<Printer className="h-4 w-4" />} onClick={() => window.print()}>Print / Save as PDF</Button>
      </div>
      <article className="mx-auto max-w-[210mm] bg-white p-8 text-[13px] leading-relaxed text-ink-900 shadow-card print:max-w-none print:p-0 print:shadow-none">
        <header className="flex items-start justify-between border-b-2 border-ink-900 pb-4">
          <div><h1 className="text-2xl font-bold tracking-wide">DELIVERY CHALLAN</h1>
            <p className="text-ink-600">Goods dispatched, not for sale.</p></div>
          <div className="text-right"><p className="text-lg font-semibold">{c.challanNo}</p><p>Date: {dateLabel(c.dispatchDate)}</p></div>
        </header>

        <section className="grid grid-cols-2 gap-6 border-b border-line py-4">
          <div>
            <p className="text-xs font-semibold uppercase text-ink-500">Deliver to</p>
            <p className="font-semibold">{c.customerName}</p>
            {c.contactPerson && <p>Attn: {c.contactPerson}{c.mobile ? ` · ${c.mobile}` : ''}</p>}
            {c.deliveryAddress && <p className="whitespace-pre-line text-ink-700">{c.deliveryAddress}</p>}
            {c.gstin && <p>GSTIN: {c.gstin}</p>}
          </div>
          <div className="text-right">
            <p><span className="text-ink-500">Order:</span> {c.orderNo}</p>
            {c.customerPoNo && <p><span className="text-ink-500">Customer PO:</span> {c.customerPoNo}</p>}
            <p><span className="text-ink-500">Project:</span> {c.projectCode} — {c.projectName}</p>
            <p><span className="text-ink-500">Transporter:</span> {c.transporter || '—'}</p>
            <p><span className="text-ink-500">Vehicle:</span> {c.vehicleNo || '—'} · <span className="text-ink-500">LR:</span> {c.lrNumber || '—'}</p>
          </div>
        </section>

        <table className="mt-4 w-full border-collapse text-left">
          <thead><tr className="border-y border-ink-900 bg-ink-50 text-xs">
            <th className="px-2 py-1.5">#</th><th className="px-2 py-1.5">Description</th><th className="px-2 py-1.5">Model</th>
            <th className="px-2 py-1.5 text-right">Quantity</th></tr></thead>
          <tbody>
            {c.items.map((l: any, n: number) => (
              <tr key={l.lineNo} className="border-b border-line">
                <td className="px-2 py-1.5">{n + 1}</td><td className="px-2 py-1.5 font-medium">{l.description}</td>
                <td className="px-2 py-1.5">{l.modelNumber ?? ''}</td>
                <td className="tabular px-2 py-1.5 text-right">{qtyLabel(l.quantity)} {l.unit}</td>
              </tr>))}
          </tbody>
        </table>
        {c.note && <p className="mt-3 text-xs text-ink-600">Note: {c.note}</p>}

        <footer className="mt-16 grid grid-cols-2 gap-10 text-xs text-ink-600">
          <div><div className="mb-1 border-t border-ink-900" />Prepared by / dispatched</div>
          <div><div className="mb-1 border-t border-ink-900" />Received in good condition — name, signature, stamp & date
            {c.receivedByName && <p className="mt-1 text-ink-900">Received by {c.receivedByName}</p>}</div>
        </footer>
      </article>
    </div>
  );
}
